import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { apiService } from "../services/api";
import { Card, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Modal } from "../components/ui/Modal";
import { ConfirmationModal } from "../components/ui/ConfirmationModal";
import { Input } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import toast from "react-hot-toast";
import {
  RectangleGroupIcon,
  PlusIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";

const GroupsManagement = () => {
  const { clubId } = useParams();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [newGroup, setNewGroup] = useState({
    name: "",
    description: "",
    color: "#3B82F6",
  });
  const [showCustomColorPicker, setShowCustomColorPicker] = useState(false);
  const [showEditCustomColorPicker, setShowEditCustomColorPicker] =
    useState(false);

  const predefinedColors = [
    { value: "#3B82F6", name: "Blue" },
    { value: "#10B981", name: "Green" },
    { value: "#F59E0B", name: "Yellow" },
    { value: "#EF4444", name: "Red" },
    { value: "#8B5CF6", name: "Purple" },
    { value: "#06B6D4", name: "Cyan" },
    { value: "#84CC16", name: "Lime" },
    { value: "#F97316", name: "Orange" },
  ];

  const loadGroups = useCallback(async () => {
    try {
      setLoading(true);
      const groupsData = await apiService.getEvaluationGroups(clubId);
      setGroups(groupsData);
    } catch (error) {
      console.error("Failed to load groups:", error);
      toast.error("Failed to load groups");
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!newGroup.name.trim()) {
      toast.error("Group name is required");
      return;
    }

    if (!isValidHexColor(newGroup.color)) {
      toast.error("Please enter a valid hex color code (e.g., #FF5733)");
      return;
    }

    try {
      const createdGroup = await apiService.createEvaluationGroup(
        clubId,
        newGroup,
      );
      setGroups([...groups, createdGroup]);
      resetNewGroupState();
      toast.success("Group created successfully!");
    } catch (error) {
      console.error("Failed to create group:", error);
      toast.error("Failed to create group");
    }
  };

  const handleEditGroup = (group) => {
    setEditingGroup({ ...group });
    setShowEditModal(true);
    // Reset custom color picker state
    const isPredefinedColor = predefinedColors.some(
      (color) => color.value === group.color,
    );
    setShowEditCustomColorPicker(!isPredefinedColor);
  };

  const handleUpdateGroup = async (e) => {
    e.preventDefault();
    if (!editingGroup.name.trim()) {
      toast.error("Group name is required");
      return;
    }

    if (!isValidHexColor(editingGroup.color)) {
      toast.error("Please enter a valid hex color code (e.g., #FF5733)");
      return;
    }

    try {
      const updatedGroup = await apiService.updateEvaluationGroup(
        clubId,
        editingGroup.id,
        {
          name: editingGroup.name,
          description: editingGroup.description,
          color: editingGroup.color,
        },
      );

      setGroups(
        groups.map((g) => (g.id === updatedGroup.id ? updatedGroup : g)),
      );
      resetEditGroupState();
      toast.success("Group updated successfully!");
    } catch (error) {
      console.error("Failed to update group:", error);
      toast.error("Failed to update group");
    }
  };

  const handleDeleteGroup = async () => {
    if (!groupToDelete) return;

    setDeleting(true);
    try {
      await apiService.deleteEvaluationGroup(clubId, groupToDelete.id);
      setGroups(groups.filter((g) => g.id !== groupToDelete.id));
      setShowDeleteModal(false);
      setGroupToDelete(null);
      toast.success("Group deleted successfully!");
    } catch (error) {
      console.error("Failed to delete group:", error);
      toast.error("Failed to delete group");
    }
    setDeleting(false);
  };

  const handleDeleteGroupClick = (group) => {
    setGroupToDelete(group);
    setShowDeleteModal(true);
  };

  const getColorStyle = (color) => ({
    backgroundColor: color,
    color: "#ffffff",
  });

  const isValidHexColor = (color) => {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
  };

  const resetNewGroupState = () => {
    setNewGroup({ name: "", description: "", color: "#3B82F6" });
    setShowCustomColorPicker(false);
    setShowCreateModal(false);
  };

  const resetEditGroupState = () => {
    setEditingGroup(null);
    setShowEditCustomColorPicker(false);
    setShowEditModal(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-slate-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Groups Management
          </h1>
          <p className="text-gray-600">
            Create and manage evaluation groups for player categorization
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <PlusIcon className="h-4 w-4 mr-2" />
          Create Group
        </Button>
      </div>

      {/* Groups List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {groups.length === 0 ? (
          <div className="col-span-full">
            <Card>
              <CardContent className="p-12 text-center">
                <RectangleGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No groups yet
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Get started by creating your first evaluation group
                </p>
                <div className="mt-6">
                  <Button onClick={() => setShowCreateModal(true)}>
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Create Group
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          groups.map((group) => (
            <Card key={group.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6 flex flex-col h-full">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: group.color }}
                    />
                    <h3 className="text-lg font-semibold text-gray-900">
                      {group.name}
                    </h3>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditGroup(group)}
                    >
                      Edit
                    </Button>

                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => handleDeleteGroupClick(group)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>

                <p className="text-gray-600 text-sm mb-4 flex-grow">
                  {group.description || "No description provided"}
                </p>

                <div className="flex items-center justify-between text-sm mt-auto">
                  <div className="flex items-center text-gray-500">
                    <UsersIcon className="h-4 w-4 mr-1" />
                    {group.player_count || 0} players
                  </div>
                  <Badge variant="outline" style={getColorStyle(group.color)}>
                    {group.name}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Group Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={resetNewGroupState}
        title="Create New Group"
      >
        <form onSubmit={handleCreateGroup} className="space-y-4">
          <Input
            placeholder="Group Name"
            value={newGroup.name}
            onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
            required
          />

          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
            placeholder="Description (optional)"
            rows={3}
            value={newGroup.description}
            onChange={(e) =>
              setNewGroup({ ...newGroup, description: e.target.value })
            }
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Color
            </label>

            {!showCustomColorPicker ? (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {predefinedColors.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      className={`w-8 h-8 rounded-full border-2 ${
                        newGroup.color === color.value
                          ? "border-gray-800"
                          : "border-gray-300"
                      }`}
                      style={{ backgroundColor: color.value }}
                      onClick={() => {
                        setNewGroup({ ...newGroup, color: color.value });
                      }}
                      title={color.name}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  className="text-sm text-slate-600 hover:text-slate-800 underline"
                  onClick={() => setShowCustomColorPicker(true)}
                >
                  Use custom color
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    value={newGroup.color}
                    onChange={(e) =>
                      setNewGroup({ ...newGroup, color: e.target.value })
                    }
                    className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <Input
                    placeholder="Enter hex color (e.g., #FF5733)"
                    value={newGroup.color}
                    onChange={(e) =>
                      setNewGroup({ ...newGroup, color: e.target.value })
                    }
                    pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
                    className="flex-1"
                  />
                  <div
                    className="w-10 h-10 rounded border border-gray-300"
                    style={{
                      backgroundColor: isValidHexColor(newGroup.color)
                        ? newGroup.color
                        : "#cccccc",
                    }}
                    title="Color preview"
                  />
                </div>
                <button
                  type="button"
                  className="text-sm text-slate-600 hover:text-slate-800 underline"
                  onClick={() => {
                    setShowCustomColorPicker(false);
                    setNewGroup({ ...newGroup, color: "#3B82F6" });
                  }}
                >
                  Use predefined colors
                </button>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={resetNewGroupState}
            >
              Cancel
            </Button>
            <Button type="submit">Create Group</Button>
          </div>
        </form>
      </Modal>

      {/* Edit Group Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={resetEditGroupState}
        title="Edit Group"
      >
        {editingGroup && (
          <form onSubmit={handleUpdateGroup} className="space-y-4">
            <Input
              placeholder="Group Name"
              value={editingGroup.name}
              onChange={(e) =>
                setEditingGroup({ ...editingGroup, name: e.target.value })
              }
              required
            />

            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
              placeholder="Description (optional)"
              rows={3}
              value={editingGroup.description}
              onChange={(e) =>
                setEditingGroup({
                  ...editingGroup,
                  description: e.target.value,
                })
              }
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Color
              </label>

              {!showEditCustomColorPicker ? (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {predefinedColors.map((color) => (
                      <button
                        key={color.value}
                        type="button"
                        className={`w-8 h-8 rounded-full border-2 ${
                          editingGroup.color === color.value
                            ? "border-gray-800"
                            : "border-gray-300"
                        }`}
                        style={{ backgroundColor: color.value }}
                        onClick={() =>
                          setEditingGroup({
                            ...editingGroup,
                            color: color.value,
                          })
                        }
                        title={color.name}
                      />
                    ))}
                  </div>
                  <button
                    type="button"
                    className="text-sm text-slate-600 hover:text-slate-800 underline"
                    onClick={() => setShowEditCustomColorPicker(true)}
                  >
                    Use custom color
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={editingGroup.color}
                      onChange={(e) =>
                        setEditingGroup({
                          ...editingGroup,
                          color: e.target.value,
                        })
                      }
                      className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                    />
                    <Input
                      placeholder="Enter hex color (e.g., #FF5733)"
                      value={editingGroup.color}
                      onChange={(e) =>
                        setEditingGroup({
                          ...editingGroup,
                          color: e.target.value,
                        })
                      }
                      pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
                      className="flex-1"
                    />
                    <div
                      className="w-10 h-10 rounded border border-gray-300"
                      style={{
                        backgroundColor: isValidHexColor(editingGroup.color)
                          ? editingGroup.color
                          : "#cccccc",
                      }}
                      title="Color preview"
                    />
                  </div>
                  <button
                    type="button"
                    className="text-sm text-slate-600 hover:text-slate-800 underline"
                    onClick={() => {
                      setShowEditCustomColorPicker(false);
                      setEditingGroup({ ...editingGroup, color: "#3B82F6" });
                    }}
                  >
                    Use predefined colors
                  </button>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={resetEditGroupState}
              >
                Cancel
              </Button>
              <Button type="submit">Update Group</Button>
            </div>
          </form>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onConfirm={handleDeleteGroup}
        onCancel={() => {
          setShowDeleteModal(false);
          setGroupToDelete(null);
        }}
        title="Delete Group"
        message={`Are you sure you want to delete "${groupToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={deleting}
        type="danger"
      />
    </div>
  );
};

export default GroupsManagement;
